import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Autocomplete,
  Avatar,
  Badge,
  Button,
  Group,
  Radio,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { Controller, useForm } from 'react-hook-form';
import { useDebouncedValue } from '@mantine/hooks';
import { HiArrowLeft } from 'react-icons/hi';
import { isMobile } from 'react-device-detect';
import useNotification from '@hooks/useNotification';
import defaultAvatar from '@assets/default-avatar.png';
import { MIN_SEARCH_CHARS, SEARCH_DEBOUNCE_MS } from '@constants/various';
import { YesNoRadioGroup } from './YesNoRadioGroup';
import type {
  EndorsementFormData,
  EndorsementModalApi,
  EventType,
  Trait,
  WorkerSuggestion,
} from './types';

const EVENTS_NUMBER_OPTIONS = [
  { value: 'N_1_2', label: '1-2' },
  { value: 'N_3_6', label: '3-6' },
  { value: 'N_7_9', label: '7-9' },
  { value: 'N_10_PLUS', label: '10+' },
];

const MAX_TRAITS = 4;

export interface Step2ContentProps {
  selectedWorker: WorkerSuggestion;
  control: ReturnType<typeof useForm<EndorsementFormData>>['control'];
  setValue: ReturnType<typeof useForm<EndorsementFormData>>['setValue'];
  watch: ReturnType<typeof useForm<EndorsementFormData>>['watch'];
  eventTypes: EventType[];
  traits: Trait[];
  api: Pick<EndorsementModalApi, 'searchBrands'>;
  isSubmitting: boolean;
  onBack: () => void;
}

export function Step2Content({
  selectedWorker,
  control,
  setValue,
  watch,
  eventTypes,
  traits,
  api,
  isSubmitting,
  onBack,
}: Step2ContentProps) {
  const { showWarningNotification } = useNotification();
  const [brandSuggestions, setBrandSuggestions] = useState<string[]>([]);

  const watchedEventTypes = watch('sharedEventsType');
  const watchedTraits = watch('endorsedTraits');
  const watchedBrand = watch('companyName');
  const [debouncedBrand] = useDebouncedValue(watchedBrand, SEARCH_DEBOUNCE_MS);

  const toggleEventType = (id: number) => {
    const current = watchedEventTypes || [];
    if (current.includes(id)) {
      setValue(
        'sharedEventsType',
        current.filter((eventId) => eventId !== id)
      );
    } else {
      setValue('sharedEventsType', [...current, id]);
    }
  };

  const toggleTrait = (id: number) => {
    const current = watchedTraits || [];
    if (current.includes(id)) {
      setValue(
        'endorsedTraits',
        current.filter((traitId) => traitId !== id)
      );
    } else if (current.length < MAX_TRAITS) {
      setValue('endorsedTraits', [...current, id]);
    } else {
      showWarningNotification(
        `You can select up to ${MAX_TRAITS} traits only.`,
        { title: 'Maximum traits reached' }
      );
    }
  };

  useEffect(() => {
    const query = debouncedBrand?.trim() ?? '';
    if (query.length < MIN_SEARCH_CHARS) {
      setBrandSuggestions([]);
      return () => {};
    }
    let cancelled = false;
    api
      .searchBrands(query)
      .then((results) => {
        if (!cancelled) setBrandSuggestions(results.map((brand) => brand.name));
      })
      .catch(() => {
        if (!cancelled) setBrandSuggestions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedBrand, api]);

  return (
    <Stack gap="md">
      {isMobile && (
        <ActionIcon variant="subtle" onClick={onBack} aria-label="Go back">
          <HiArrowLeft size={24} color="black" />
        </ActionIcon>
      )}

      <Group gap="md">
        <Avatar
          src={selectedWorker.profileImageUrl || defaultAvatar}
          size={60}
          radius={4}
        />
        <div>
          <Text size="lg" fw={600}>
            {selectedWorker.name}
          </Text>
          <Text size="sm" c="dimmed">
            {selectedWorker.email || ''}
          </Text>
        </div>
      </Group>

      <div>
        <Text size="sm" fw={500} mb={4}>
          Did they manage a program for you? *
        </Text>
        <Controller
          control={control}
          name="isEndorsedManager"
          render={({ field }) => (
            <YesNoRadioGroup value={field.value} onChange={field.onChange} />
          )}
        />
      </div>

      <div>
        <Text size="sm" fw={500} mb={4}>
          Number of shared events *
        </Text>
        <Controller
          control={control}
          name="sharedEventsNumber"
          rules={{ required: 'Please select the number of shared events' }}
          render={({ field }) => (
            <Group mt="xs">
              {EVENTS_NUMBER_OPTIONS.map((option) => (
                <Radio
                  key={option.value}
                  value={option.value}
                  label={option.label}
                  checked={field.value === option.value}
                  onChange={() => field.onChange(option.value)}
                />
              ))}
            </Group>
          )}
        />
      </div>

      <div>
        <Text size="sm" fw={500} mb={4}>
          Event types
        </Text>
        <Group gap="xs">
          {eventTypes.map((eventType) => {
            const isSelected = (watchedEventTypes || []).includes(eventType.id);
            return (
              <Badge
                key={eventType.id}
                variant={isSelected ? 'filled' : 'light'}
                color={isSelected ? 'green' : 'gray'}
                style={{ cursor: 'pointer' }}
                onClick={() => toggleEventType(eventType.id)}
              >
                {eventType.name}
              </Badge>
            );
          })}
        </Group>
      </div>

      <div>
        <Text size="sm" fw={500} mb={4}>
          Traits ({(watchedTraits || []).length}/{MAX_TRAITS} selected)
        </Text>
        <Group gap="xs">
          {traits.map((trait) => {
            const isSelected = (watchedTraits || []).includes(trait.id);
            return (
              <Badge
                key={trait.id}
                variant={isSelected ? 'filled' : 'light'}
                color={isSelected ? 'green' : 'gray'}
                style={{ cursor: 'pointer' }}
                onClick={() => toggleTrait(trait.id)}
              >
                {trait.name}
              </Badge>
            );
          })}
        </Group>
      </div>

      <Controller
        control={control}
        name="companyName"
        rules={{ required: 'Brand name is required' }}
        render={({ field, fieldState }) => (
          <Autocomplete
            label="Brand that you've worked on/for with this person *"
            placeholder="Search brands..."
            data={brandSuggestions}
            value={field.value}
            onChange={field.onChange}
            error={fieldState.error?.message}
            comboboxProps={{ zIndex: 1200, withinPortal: true }} // Ensure suggestions dropdown appears above the modal
          />
        )}
      />

      <Controller
        control={control}
        name="reviewDescription"
        render={({ field }) => (
          <Textarea
            label="Review (optional)"
            placeholder="Share your experience working with this person..."
            maxLength={300}
            value={field.value || ''}
            onChange={field.onChange}
          />
        )}
      />

      <Text size="sm" fs="italic" c="dimmed">
        Endorsements help workers build their professional reputation on Trusted
        Herd. Your endorsement will be visible on their profile.
      </Text>

      <Group justify="flex-end">
        {!isMobile && (
          <Button variant="default" onClick={onBack}>
            Back
          </Button>
        )}
        <Button type="submit" loading={isSubmitting}>
          Submit
        </Button>
      </Group>
    </Stack>
  );
}
